import { useEffect, useState } from "react";
import { User as UserIcon, LogOut, Trash2, Loader2, Settings } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import type { User } from "@supabase/supabase-js";

type Props = {
  onSignedOut: () => void;
};

export function ProfileMenu({ onSignedOut }: Props) {
  const [user, setUser] = useState<User | null>(null);

  const [profileOpen, setProfileOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const [fullName, setFullName] = useState("");
  const [virksomhed, setVirksomhed] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    supabase.auth.getUser().then(({ data }) => {
      if (!mounted) return;
      const u = data.user;
      setUser(u);
      const meta = (u?.user_metadata ?? {}) as Record<string, unknown>;
      setFullName((meta.full_name as string) || (meta.navn as string) || "");
      setVirksomhed((meta.virksomhed as string) || "");
    });
    return () => {
      mounted = false;
    };
  }, []);

  const meta = (user?.user_metadata ?? {}) as Record<string, unknown>;
  const displayName = (meta.full_name as string) || (meta.navn as string) || user?.email || "Profil";

  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      const { data, error } = await supabase.auth.updateUser({
        data: {
          ...(meta as object),
          full_name: fullName || null,
          virksomhed: virksomhed || null,
        },
      });
      if (error) throw error;
      setUser(data.user);
      setProfileOpen(false);
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : "Kunne ikke gemme");
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    onSignedOut();
  };

  const handleDelete = async () => {
    setDeleting(true);
    setDeleteError(null);
    try {
      const { deleteMyAccount } = await import("@/lib/admin.functions");
      await deleteMyAccount();
      await supabase.auth.signOut();
      setConfirmDelete(false);
      onSignedOut();
    } catch (e) {
      setDeleteError(e instanceof Error ? e.message : "Kunne ikke slette kontoen");
    } finally {
      setDeleting(false);
    }
  };

  if (!user) return null;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            className="inline-flex items-center gap-2 rounded-full border border-foreground/15 px-3 py-1.5 text-sm font-semibold hover:bg-card/60 transition"
            aria-label="Profil menu"
          >
            <UserIcon className="h-4 w-4" />
            <span className="hidden sm:inline max-w-[140px] truncate">{displayName}</span>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel className="truncate">{user.email}</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={() => setProfileOpen(true)}>
            <Settings className="mr-2 h-4 w-4" /> Min profil
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={handleLogout}>
            <LogOut className="mr-2 h-4 w-4" /> Log ud
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            className="text-destructive focus:text-destructive"
            onSelect={(e) => {
              e.preventDefault();
              setConfirmDelete(true);
            }}
          >
            <Trash2 className="mr-2 h-4 w-4" /> Slet min konto
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Profile dialog */}
      <Dialog open={profileOpen} onOpenChange={setProfileOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Min profil</DialogTitle>
            <DialogDescription>Opdater dine oplysninger.</DialogDescription>
          </DialogHeader>

          <div className="grid gap-3">
            <div className="grid gap-1.5">
              <Label htmlFor="pm-name">Fulde navn</Label>
              <Input
                id="pm-name"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="Dit navn"
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="pm-company">Virksomhed</Label>
              <Input
                id="pm-company"
                value={virksomhed}
                onChange={(e) => setVirksomhed(e.target.value)}
                placeholder="Virksomhedens navn"
              />
            </div>
            <div className="grid gap-1.5">
              <Label>E-mail</Label>
              <Input value={user.email ?? ""} disabled />
            </div>
          </div>

          {saveError && <p className="text-sm text-destructive">{saveError}</p>}

          <DialogFooter>
            <Button variant="ghost" onClick={() => setProfileOpen(false)} disabled={saving}>
              Annuller
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Gem ændringer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog
        open={confirmDelete}
        onOpenChange={(open) => {
          if (!deleting) setConfirmDelete(open);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Slet din konto?</AlertDialogTitle>
            <AlertDialogDescription>
              Denne handling kan ikke fortrydes. Din konto og tilhørende data slettes permanent.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {deleteError && <p className="text-sm text-destructive">{deleteError}</p>}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Annuller</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleDelete();
              }}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sletter…
                </>
              ) : (
                <>
                  <Trash2 className="mr-2 h-4 w-4" /> Slet permanent
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
